import React, { useState, useEffect } from 'react';
import SuperAdminLayout from '../../../components/hostel/superadmin/SuperAdminLayout';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const SuperAdminRentManagement = ({ onNavigate, userInfo }) => {
  const [rentConfigs, setRentConfigs] = useState([]);
  const [rentLoading, setRentLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [hostels, setHostels] = useState([]);
  const [selectedHostelFilter, setSelectedHostelFilter] = useState('');

  const [currentConfig, setCurrentConfig] = useState({
    batch_name: '',
    hostel_id: '',
    hostel_name: '',
    room_type: '',
    sharing_type: '',
    is_ac: 0,
    room_rent: '',
    food_rent: '',
    laundry_charges: '',
    maintenance_charges: '',
    security_deposit: '',
    effective_from: '',
    effective_to: '',
    is_active: 1,
    remarks: ''
  });

  useEffect(() => {
    if (userInfo?.userId) {
      fetchRentConfigs();
      fetchHostels();
    }
  }, [userInfo]);

  const fetchRentConfigs = async (hostelId = '') => {
    try {
      setRentLoading(true);
      const url = hostelId
        ? `${API_URL}/superadmin/rent-configs/${userInfo.userId}?hostelId=${hostelId}`
        : `${API_URL}/superadmin/rent-configs/${userInfo.userId}`;

      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setRentConfigs(data.configs || []);
      }
    } catch (err) {
      console.error('Error fetching rent configs:', err);
    } finally {
      setRentLoading(false);
    }
  };

  const fetchHostels = async () => {
    try {
      const response = await fetch(`${API_URL}/superadmin/all-hostels-for-rent/${userInfo.userId}`);
      const data = await response.json();
      if (data.success) {
        setHostels(data.hostels || []);
      }
    } catch (err) {
      console.error('Error fetching hostels:', err);
    }
  };

  const handleFilterChange = (hostelId) => {
    setSelectedHostelFilter(hostelId);
    fetchRentConfigs(hostelId);
  };

  const handleAddNew = () => {
    setCurrentConfig({
      batch_name: '',
      hostel_id: '',
      hostel_name: '',
      room_type: '',
      sharing_type: '',
      is_ac: 0,
      room_rent: '',
      food_rent: '',
      laundry_charges: '',
      maintenance_charges: '',
      security_deposit: '',
      effective_from: '',
      effective_to: '',
      is_active: 1,
      remarks: ''
    });
    setEditMode(false);
    setShowModal(true);
  };

  const handleEdit = (config) => {
    setCurrentConfig({
      ...config,
      effective_from: config.effective_from?.split('T')[0] || '',
      effective_to: config.effective_to?.split('T')[0] || ''
    });
    setEditMode(true);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this rent configuration?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/superadmin/rent-configs/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId: userInfo.userId })
      });

      const data = await response.json();
      if (data.success) {
        alert('Rent configuration deleted successfully!');
        fetchRentConfigs(selectedHostelFilter);
      } else {
        alert(data.message || 'Failed to delete');
      }
    } catch (err) {
      console.error('Error deleting rent config:', err);
      alert('Failed to delete rent configuration');
    }
  };

  const handleSave = async () => {
    try {
      let hostelName = currentConfig.hostel_name;
      if (currentConfig.hostel_id && !hostelName) {
        const selectedHostel = hostels.find(h => h.hostel_id == currentConfig.hostel_id);
        hostelName = selectedHostel?.hostel_name || '';
      }

      const url = editMode
        ? `${API_URL}/superadmin/rent-configs/${currentConfig.id}`
        : `${API_URL}/superadmin/rent-configs`;

      const method = editMode ? 'PUT' : 'POST';

      const payload = {
        ...currentConfig,
        hostel_name: hostelName,
        created_by: userInfo.userId,
        userId: userInfo.userId
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        alert(editMode ? 'Updated successfully!' : 'Created successfully!');
        setShowModal(false);
        fetchRentConfigs(selectedHostelFilter);
      } else {
        alert(data.message || 'Operation failed');
      }
    } catch (err) {
      console.error('Error saving rent config:', err);
      alert('Failed to save rent configuration');
    }
  };

  const handleInputChange = (field, value) => {
    setCurrentConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <SuperAdminLayout
      onNavigate={onNavigate}
      userInfo={userInfo}
      activePage="hostel-superadmin-manage-rent"
      pageTitle="Rent Management 💰"
    >
      <style>{`
        .hero-section {
          background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
          border-radius: 24px;
          padding: 48px 36px;
          margin-bottom: 40px;
          border: 1px solid rgba(82,171,152,0.15);
          box-shadow: 0 2px 8px rgba(43,103,119,0.06);
          position: relative;
          overflow: hidden;
        }

        .hero-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
        }

        .hero-content {
          position: relative;
          z-index: 1;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .hero-title {
          font-size: 36px;
          font-weight: 900;
          color: #2b6777;
          margin-bottom: 10px;
          letter-spacing: -0.5px;
        }

        .hero-subtitle {
          font-size: 16px;
          color: rgba(43,103,119,0.6);
          font-weight: 500;
        }

        .add-btn {
          padding: 18px 32px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          border: none;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 10px;
          transition: all 0.3s;
          box-shadow: 0 6px 24px rgba(43,103,119,0.3);
        }

        .add-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 35px rgba(43,103,119,0.4);
        }

        .filter-section {
          background: #ffffff;
          border-radius: 20px;
          padding: 24px;
          margin-bottom: 24px;
          border: 1px solid rgba(82,171,152,0.15);
          box-shadow: 0 2px 8px rgba(43,103,119,0.06);
        }

        .filter-select {
          width: 300px;
          padding: 14px 16px;
          border: 1.5px solid rgba(82,171,152,0.25);
          border-radius: 12px;
          font-size: 14px;
          color: #2b6777;
          font-weight: 600;
          background: rgba(82,171,152,0.05);
          cursor: pointer;
          transition: all 0.3s;
        }

        .table-card {
          background: #ffffff;
          border-radius: 24px;
          padding: 32px;
          border: 1px solid rgba(82,171,152,0.15);
          box-shadow: 0 2px 8px rgba(43,103,119,0.06);
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table thead {
          background: linear-gradient(135deg, rgba(43,103,119,0.05) 0%, rgba(82,171,152,0.05) 100%);
        }

        .data-table th {
          padding: 16px 20px;
          text-align: left;
          font-size: 12px;
          font-weight: 700;
          color: rgba(43,103,119,0.6);
          text-transform: uppercase;
          letter-spacing: 0.8px;
          border-bottom: 2px solid rgba(82,171,152,0.15);
        }

        .data-table tbody tr {
          border-bottom: 1px solid rgba(82,171,152,0.08);
          transition: all 0.3s;
        }

        .data-table tbody tr:hover {
          background: rgba(82,171,152,0.05);
        }

        .data-table td {
          padding: 20px;
          font-size: 14px;
          color: #2b6777;
          font-weight: 500;
        }

        .status-badge {
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          display: inline-block;
        }

        .status-active {
          background: rgba(82,171,152,0.15);
          color: #52ab98;
          border: 1.5px solid rgba(82,171,152,0.25);
        }

        .status-inactive {
          background: rgba(235,87,87,0.15);
          color: #eb5757;
          border: 1.5px solid rgba(235,87,87,0.25);
        }

        .action-buttons {
          display: flex;
          gap: 10px;
        }

        .action-btn-table {
          padding: 10px 18px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          border: none;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .edit-btn {
          background: rgba(82,171,152,0.1);
          color: #52ab98;
          border: 1.5px solid rgba(82,171,152,0.2);
        }

        .edit-btn:hover {
          background: #52ab98;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(82,171,152,0.3);
        }

        .delete-btn {
          background: rgba(235,87,87,0.1);
          color: #eb5757;
          border: 1.5px solid rgba(235,87,87,0.2);
        }

        .delete-btn:hover {
          background: #eb5757;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(235,87,87,0.3);
        }

        .loading-state, .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid rgba(82,171,152,0.1);
          border-top-color: #52ab98;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .loading-text, .empty-state-title {
          font-size: 16px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 8px;
        }

        .empty-state-icon {
          font-size: 64px;
          margin-bottom: 16px;
          opacity: 0.5;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(43,103,119,0.6);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 200;
          padding: 20px;
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .modal-content {
          background: #ffffff;
          border-radius: 24px;
          padding: 0;
          max-width: 800px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 24px 64px rgba(43,103,119,0.3);
          animation: slideUp 0.3s ease;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .modal-header {
          padding: 28px 32px;
          border-bottom: 2px solid rgba(82,171,152,0.15);
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: linear-gradient(135deg, rgba(43,103,119,0.05) 0%, rgba(82,171,152,0.05) 100%);
        }

        .modal-title {
          font-size: 24px;
          font-weight: 900;
          color: #2b6777;
        }

        .close-modal-btn {
          background: rgba(235,87,87,0.1);
          border: 1.5px solid rgba(235,87,87,0.2);
          color: #eb5757;
          font-size: 24px;
          width: 40px;
          height: 40px;
          border-radius: 10px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 24px;
          padding: 32px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .form-group.full-width {
          grid-column: 1 / -1;
        }

        .form-label {
          font-size: 13px;
          font-weight: 700;
          color: #2b6777;
          letter-spacing: 0.3px;
        }

        .form-input, .form-select, .form-textarea {
          padding: 14px 16px;
          background: rgba(82,171,152,0.05);
          border: 1.5px solid rgba(82,171,152,0.2);
          border-radius: 12px;
          color: #2b6777;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.3s;
          font-family: 'Inter', sans-serif;
        }

        .form-input:focus, .form-select:focus, .form-textarea:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 3px rgba(82,171,152,0.1);
          background: #ffffff;
        }
        
        .form-textarea {
            resize: vertical;
            min-height: 100px;
        }

        .form-actions {
          padding: 24px 32px;
          border-top: 2px solid rgba(82,171,152,0.15);
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          background: rgba(82,171,152,0.02);
        }

        .btn-primary, .btn-secondary {
          padding: 14px 28px;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
          border: none;
        }

        .btn-primary {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(43,103,119,0.3);
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(43,103,119,0.4);
        }

        .btn-secondary {
          background: rgba(82,171,152,0.1);
          color: #2b6777;
          border: 1.5px solid rgba(82,171,152,0.2);
        }

        .btn-secondary:hover {
          background: rgba(82,171,152,0.15);
        }

        @media (max-width: 1024px) {
            .hero-content {
                flex-direction: column;
                align-items: flex-start;
                gap: 20px;
            }
             .add-btn {
                width: 100%;
                justify-content: center;
             }
        }

        @media (max-width: 768px) {
            .form-grid {
                grid-template-columns: 1fr;
            }
             .action-buttons {
                flex-direction: column;
            }
        }
      `}</style>

      <div className="hero-section">
        <div className="hero-content">
          <div>
            <h1 className="hero-title">Rent Management 💰</h1>
            <p className="hero-subtitle">
              Configure and manage rent structures for all hostels across different batches
            </p>
          </div>
          <button className="add-btn" onClick={handleAddNew}>
            <span>➕</span>
            Add New Configuration
          </button>
        </div>
      </div>

      <div className="filter-section">
        <label className="filter-label">Filter by Hostel</label>
        <select
          className="filter-select"
          value={selectedHostelFilter}
          onChange={(e) => handleFilterChange(e.target.value)}
        >
          <option value="">All Hostels</option>
          {hostels.map(hostel => (
            <option key={hostel.hostel_id} value={hostel.hostel_id}>
              {hostel.hostel_name}
            </option>
          ))}
        </select>
      </div>

      <div className="table-card">
        {rentLoading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading rent configurations...</div>
          </div>
        ) : rentConfigs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">💰</div>
            <div className="empty-state-title">No Rent Configurations Found</div>
            <div className="empty-state-text">
              Click "Add New Configuration" to create your first rent structure
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Hostel</th>
                  <th>Batch Name</th>
                  <th>Room Type</th>
                  <th>Sharing</th>
                  <th>AC/Non-AC</th>
                  <th>Room Rent</th>
                  <th>Food Rent</th>
                  <th>Total Rent</th>
                  <th>Effective From</th>
                  <th>Effective To</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rentConfigs.map((config) => (
                  <tr key={config.id}>
                    <td style={{ fontWeight: '700' }}>
                      {config.hostel_display_name || config.hostel_name || 'All Hostels'}
                    </td>
                    <td style={{ fontWeight: '700' }}>{config.batch_name}</td>
                    <td>{config.room_type || 'N/A'}</td>
                    <td>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: '#e0f2fe',
                        color: '#0369a1',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {config.sharing_type?.toUpperCase() || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: config.is_ac ? '#dcfce7' : '#fee2e2',
                        color: config.is_ac ? '#166534' : '#991b1b',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {config.is_ac ? '❄️ AC' : '🌡️ Non-AC'}
                      </span>
                    </td>
                    <td>₹{parseFloat(config.room_rent).toLocaleString()}</td>
                    <td>₹{parseFloat(config.food_rent).toLocaleString()}</td>
                    <td style={{ fontWeight: '800', color: '#2b6777' }}>
                      ₹{parseFloat(config.total_rent).toLocaleString()}
                    </td>
                    <td>{new Date(config.effective_from).toLocaleDateString()}</td>
                    <td>
                      {config.effective_to
                        ? new Date(config.effective_to).toLocaleDateString()
                        : 'Ongoing'}
                    </td>
                    <td>
                      <span className={`status-badge ${config.is_active ? 'status-active' : 'status-inactive'}`}>
                        {config.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="action-btn-table edit-btn"
                          onClick={() => handleEdit(config)}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          className="action-btn-table delete-btn"
                          onClick={() => handleDelete(config.id)}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                {editMode ? 'Edit Rent Configuration' : 'Add New Rent Configuration'}
              </div>
              <button className="close-modal-btn" onClick={() => setShowModal(false)}>
                ✕
              </button>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Hostel</label>
                <select
                  className="form-select"
                  value={currentConfig.hostel_id}
                  onChange={(e) => handleInputChange('hostel_id', e.target.value)}
                >
                  <option value="">All Hostels</option>
                  {hostels.map(hostel => (
                    <option key={hostel.hostel_id} value={hostel.hostel_id}>
                      {hostel.hostel_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Batch Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={currentConfig.batch_name}
                  onChange={(e) => handleInputChange('batch_name', e.target.value)}
                  placeholder="e.g., 2024, Batch 2024"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Room Type *</label>
                <input
                  type="text"
                  className="form-input"
                  value={currentConfig.room_type}
                  onChange={(e) => handleInputChange('room_type', e.target.value)}
                  placeholder="e.g., Single Room, Double AC Room"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Sharing Type *</label>
                <select
                  className="form-select"
                  value={currentConfig.sharing_type}
                  onChange={(e) => handleInputChange('sharing_type', e.target.value)}
                >
                  <option value="">Select Sharing Type</option>
                  <option value="single">Single</option>
                  <option value="twin">Twin (2 Bed)</option>
                  <option value="triple">Triple (3 Bed)</option>
                  <option value="four">Four (4 Bed)</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">AC/Non-AC *</label>
                <select
                  className="form-select"
                  value={currentConfig.is_ac}
                  onChange={(e) => handleInputChange('is_ac', parseInt(e.target.value))}
                >
                  <option value={0}>Non-AC</option>
                  <option value={1}>AC</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Room Rent (₹) *</label>
                <input
                  type="number"
                  className="form-input"
                  value={currentConfig.room_rent}
                  onChange={(e) => handleInputChange('room_rent', e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Food Rent (₹) *</label>
                <input
                  type="number"
                  className="form-input"
                  value={currentConfig.food_rent}
                  onChange={(e) => handleInputChange('food_rent', e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Laundry Charges (₹)</label>
                <input
                  type="number"
                  className="form-input"
                  value={currentConfig.laundry_charges}
                  onChange={(e) => handleInputChange('laundry_charges', e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Maintenance Charges (₹)</label>
                <input
                  type="number"
                  className="form-input"
                  value={currentConfig.maintenance_charges}
                  onChange={(e) => handleInputChange('maintenance_charges', e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Security Deposit (₹)</label>
                <input
                  type="number"
                  className="form-input"
                  value={currentConfig.security_deposit}
                  onChange={(e) => handleInputChange('security_deposit', e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={currentConfig.is_active}
                  onChange={(e) => handleInputChange('is_active', parseInt(e.target.value))}
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Effective From *</label>
                <input
                  type="date"
                  className="form-input"
                  value={currentConfig.effective_from}
                  onChange={(e) => handleInputChange('effective_from', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Effective To</label>
                <input
                  type="date"
                  className="form-input"
                  value={currentConfig.effective_to}
                  onChange={(e) => handleInputChange('effective_to', e.target.value)}
                />
              </div>

              <div className="form-group full-width">
                <label className="form-label">Remarks</label>
                <textarea
                  className="form-textarea"
                  value={currentConfig.remarks}
                  onChange={(e) => handleInputChange('remarks', e.target.value)}
                  placeholder="Any additional notes..."
                />
              </div>
            </div>

            <div className="form-actions">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSave}>
                {editMode ? 'Update Configuration' : 'Save Configuration'}
              </button>
            </div>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminRentManagement;